// // /**
// //  * Auth Slice
// //  * Why: Manage authentication state manually
// //  * What: Users data, login status,loading status
// //  * Benefit: Centralized auth state accessible anywhere
// //  */

// // import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
// // import type { IAuthState, IUser } from "./types";

// // /**
// //  * Initial State
// //  */
// // const initialState: IAuthState = {
// //   user: null,
// //   isLoading: false,
// //   isAuthenticating: true,
// //   isAuthenticated: false,
// //   error: null,
// // };

// // const authSlice = createSlice({
// //   name: "auth",
// //   initialState,
// //   reducers: {
// //     /**
// //      * Clear auth error
// //      */
// //     clearError: (state) => {
// //       state.error = null;
// //     },
// //     /**
// //      * Set user manually (e.g after token refresh)
// //      *
// //      */
// //     setUser: (state, action: PayloadAction<IUser>) => {
// //       state.user = action.payload;
// //       state.isAuthenticated = true;
// //       state.isAuthenticating = false;
// //     },
// //     clearUser: (state) => {
// //       state.user = null;
// //       state.isAuthenticated = false;
// //       state.isAuthenticating = false;
// //     },
// //   },
// // });

// // // Export actions
// // export const { clearError, setUser, clearUser } = authSlice.actions;
// // export default authSlice.reducer;

// /**
//  * Auth Slice
//  * Why: Manage authentication state manually
//  * What: User data, login status, loading status, AND the session version
//  * Benefit: Centralized auth state accessible anywhere
//  *
//  * ============================================================================
//  * WHAT CHANGED FROM YOURS
//  * ============================================================================
//  *   + sessionVersion / sessionType on state   (the heart of the whole system)
//  *   + setSessionVersion / setSessionType actions
//  *   + sessionInvalidated action                (the interceptor dispatches this)
//  *   ~ setUser now ALSO stamps version+type when they are present on the payload
//  *   ~ clearUser now resets version+type too    (or a stale version outlives logout)
//  */

// import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
// import type { IAuthState, IUser, SessionType } from "./types";

// const initialState: IAuthState = {
//   user: null,
//   isLoading: false,
//   isAuthenticating: true,
//   isAuthenticated: false,
//   error: null,

//   // ── session versioning ──
//   // null = "we have not heard from the server yet". Every consumer must treat
//   // null as unknown, NEVER as "changed". selectIsInvoiceStale depends on this.
//   sessionVersion: null,
//   sessionType: null,
// };

// const authSlice = createSlice({
//   name: "auth",
//   initialState,
//   reducers: {
//     clearError: (state) => {
//       state.error = null;
//     },

//     /**
//      * Set user. Optionally carries the session version+type from the same
//      * response (verifyToken and the /session/status poll both include them).
//      *
//      * Stamping them HERE, in the same reducer that sets the user, guarantees the
//      * two never drift — you cannot have a user without the version that user was
//      * authenticated under.
//      */
//     setUser: (
//       state,
//       action: PayloadAction<
//         | IUser
//         | { user: IUser; sessionVersion?: number; sessionType?: SessionType }
//       >,
//     ) => {
//       // Accept BOTH shapes so existing `dispatch(setUser(user))` calls keep
//       // working AND the new `dispatch(setUser({ user, sessionVersion }))` works.
//       const payload = action.payload;
//       const isWrapped =
//         payload && typeof payload === "object" && "user" in payload;

//       const user = (
//         isWrapped ? (payload as { user: IUser }).user : payload
//       ) as IUser;

//       state.user = user;
//       state.isAuthenticated = true;
//       state.isAuthenticating = false;

//       if (isWrapped) {
//         const w = payload as {
//           sessionVersion?: number;
//           sessionType?: SessionType;
//         };
//         if (typeof w.sessionVersion === "number")
//           state.sessionVersion = w.sessionVersion;
//         if (w.sessionType) state.sessionType = w.sessionType;
//       }
//     },

//     /** The session poll uses this to advance the version without touching user. */
//     setSessionVersion: (state, action: PayloadAction<number>) => {
//       state.sessionVersion = action.payload;
//     },

//     setSessionType: (state, action: PayloadAction<SessionType>) => {
//       state.sessionType = action.payload;
//     },

//     /**
//      * Fired by the HTTP interceptor on a 401, and by the cross-tab broadcast
//      * handler when another tab logs out. It is the SINGLE state transition that
//      * means "this session is over".
//      *
//      * It does NOT navigate — a reducer must be pure. SessionWatcher watches for
//      * isAuthenticated flipping to false and does the navigation.
//      */
//     sessionInvalidated: (state) => {
//       state.user = null;
//       state.isAuthenticated = false;
//       state.isAuthenticating = false;
//       state.sessionVersion = null;
//       state.sessionType = null;
//     },

//     clearUser: (state) => {
//       state.user = null;
//       state.isAuthenticated = false;
//       state.isAuthenticating = false;
//       // ▼ these two were the omission that let a stale version survive logout
//       state.sessionVersion = null;
//       state.sessionType = null;
//     },
//   },
// });

// export const {
//   clearError,
//   setUser,
//   setSessionVersion,
//   setSessionType,
//   sessionInvalidated,
//   clearUser,
// } = authSlice.actions;

// export default authSlice.reducer;

// ============================================================================
// DESTINATION:  src/store/slices/auth/authSlice.ts
// ACTION:       REPLACE your existing file
// ============================================================================

/**
 * Auth Slice
 * Why: Manage authentication state manually
 * What: User data, login status, loading status, AND the session version
 * Benefit: Centralized auth state accessible anywhere
 *
 * ============================================================================
 * WHAT CHANGED FROM YOURS
 * ============================================================================
 *   + sessionVersion / sessionType on state   (the heart of the whole system)
 *   + setSessionVersion / setSessionType actions
 *   + sessionInvalidated action                (the interceptor dispatches this)
 *   ~ setUser now ALSO stamps version+type when they are present on the payload
 *   ~ clearUser now resets version+type too    (or a stale version outlives logout)
 */

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { IAuthState, IUser, SessionType } from "./types";

const initialState: IAuthState = {
  user: null,
  isLoading: false,
  isAuthenticating: true,
  isAuthenticated: false,
  error: null,

  // ── session versioning ──
  // null = "we have not heard from the server yet". Every consumer must treat
  // null as unknown, NEVER as "changed". selectIsInvoiceStale depends on this.
  sessionVersion: null,
  sessionType: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Set user. Optionally carries the session version+type from the same
     * response (verifyToken and the /session/status poll both include them).
     *
     * Stamping them HERE, in the same reducer that sets the user, guarantees the
     * two never drift — you cannot have a user without the version that user was
     * authenticated under.
     */
    setUser: (
      state,
      action: PayloadAction<
        | IUser
        | { user: IUser; sessionVersion?: number; sessionType?: SessionType }
      >,
    ) => {
      // Accept BOTH shapes so existing `dispatch(setUser(user))` calls keep
      // working AND the new `dispatch(setUser({ user, sessionVersion }))` works.
      const payload = action.payload;
      const isWrapped =
        payload && typeof payload === "object" && "user" in payload;

      const user = (
        isWrapped ? (payload as { user: IUser }).user : payload
      ) as IUser;

      state.user = user;
      state.isAuthenticated = true;
      state.isAuthenticating = false;

      if (isWrapped) {
        const w = payload as {
          sessionVersion?: number;
          sessionType?: SessionType;
        };
        if (typeof w.sessionVersion === "number")
          state.sessionVersion = w.sessionVersion;
        if (w.sessionType) state.sessionType = w.sessionType;
      }
    },

    /** The session poll uses this to advance the version without touching user. */
    setSessionVersion: (state, action: PayloadAction<number>) => {
      state.sessionVersion = action.payload;
    },

    setSessionType: (state, action: PayloadAction<SessionType>) => {
      state.sessionType = action.payload;
    },

    /**
     * Fired by the HTTP interceptor on a 401, and by the cross-tab broadcast
     * handler when another tab logs out. It is the SINGLE state transition that
     * means "this session is over".
     *
     * It does NOT navigate — a reducer must be pure. SessionWatcher watches for
     * isAuthenticated flipping to false and does the navigation.
     */
    sessionInvalidated: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isAuthenticating = false;
      state.sessionVersion = null;
      state.sessionType = null;
    },

    clearUser: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isAuthenticating = false;
      // ▼ these two were the omission that let a stale version survive logout
      state.sessionVersion = null;
      state.sessionType = null;
    },
  },
});

export const {
  clearError,
  setUser,
  setSessionVersion,
  setSessionType,
  sessionInvalidated,
  clearUser,
} = authSlice.actions;

export default authSlice.reducer;
